import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Apply from "@/pages/Apply";
import Serve from "@/pages/Serve";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import MembershipCheckout from "@/pages/MembershipCheckout";
import CounselorKYC from "@/pages/CounselorKYC";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Book from "@/pages/Book";
import ClientDashboard from "@/pages/ClientDashboard";
import CounselorDashboard from "@/pages/CounselorDashboard";
import VideoSession from "@/pages/VideoSession";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";

function dashboardPathFor(user) {
  if (user.role === "employee") return "/employee";
  if (user.role === "counselor") return "/counselor";
  return "/dashboard";
}

// A valid session cookie lasts a full year and only ends on explicit logout —
// so re-opening the site should land a signed-in user straight on their
// dashboard instead of the generic landing/login pages.
function HomeGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  const bypassActive = !!user?.is_lead && sessionStorage.getItem("counseltaria_bypass_generate") === "1";
  if (user && !bypassActive) return <Navigate to={dashboardPathFor(user)} replace />;
  return <Landing />;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={dashboardPathFor(user)} replace />;
  return <Login />;
}

function AppRouter() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomeGate />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/serve" element={<Serve />} />
        <Route path="/employee" element={<ProtectedRoute role="employee"><EmployeeDashboard /></ProtectedRoute>} />
        <Route path="/login" element={<LoginGate />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/auth/callback" element={<GoogleAuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/book" element={<ProtectedRoute><Book /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
        <Route path="/counselor" element={<ProtectedRoute role="counselor"><CounselorDashboard /></ProtectedRoute>} />
        <Route path="/counselor/membership" element={<ProtectedRoute role="counselor"><MembershipCheckout /></ProtectedRoute>} />
        <Route path="/counselor/kyc" element={<ProtectedRoute role="counselor"><CounselorKYC /></ProtectedRoute>} />
        <Route path="/session/:id" element={<ProtectedRoute><VideoSession /></ProtectedRoute>} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund" element={<Refund />} />
      </Routes>
    </Layout>
  );
}

// Confirmed permanent production domain. https://counsel-connect-128.emergent.host
// is the platform's default deployment alias for this same app and is kept working
// only as a legacy/transitional entry point - it is bounced to the canonical domain
// below BEFORE anything else renders, so cookies/session calls always run same-origin
// on counseltaria.com (this also sidesteps a platform edge CORS quirk that rewrites
// cross-origin credentialed responses). Does not affect preview (different hostname).
const LEGACY_PRODUCTION_HOST = "counsel-connect-128.emergent.host";
const CANONICAL_PRODUCTION_ORIGIN = "https://counseltaria.com";

function App() {
  if (typeof window !== "undefined" && window.location.hostname === LEGACY_PRODUCTION_HOST) {
    window.location.replace(
      CANONICAL_PRODUCTION_ORIGIN + window.location.pathname + window.location.search + window.location.hash
    );
    return null;
  }
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
